import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Footer } from '@/components/Footer';
import { 
  Plus, 
  FileText, 
  Clock, 
  CheckCircle2, 
  CreditCard,
  LogOut,
  Settings,
  ChevronRight,
  Leaf,
  MapPin,
  Calendar,
  Globe,
  Mail,
  Download,
  MessageCircle,
  Phone,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

export const Dashboard = () => {
  const { user, logout, setUser, api } = useAuth();
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('status');

  useEffect(() => {
    const init = async () => {
      // If we don't have user data, fetch it
      if (!user) {
        try {
          const response = await api.get('/api/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Failed to get user:', error);
          if (error.response?.status === 401) {
            localStorage.removeItem('session_token');
            navigate('/');
            return;
          }
        }
      }
      
      // Fetch consultations
      fetchConsultations();
    };
    
    init();
  }, []);

  const fetchConsultations = async () => {
    try {
      const response = await api.get('/api/consultations');
      setConsultations(response.data);
    } catch (error) {
      console.error('Error fetching consultations:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
        navigate('/');
        return;
      }
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const getStatusInfo = (consultation) => {
    const status = consultation.status;
    const paymentStatus = consultation.payment_status;

    if (status === 'diagnosed' || status === 'completed') {
      return { label: 'Completed', color: 'emerald', icon: CheckCircle2 };
    }
    if (paymentStatus === 'pending') {
      return { label: 'Payment Pending', color: 'amber', icon: CreditCard };
    }
    if (status === 'paid' || status === 'questions_completed') {
      return { label: 'In Progress', color: 'blue', icon: Clock };
    }
    return { label: status, color: 'stone', icon: Clock };
  };

  const getNextAction = (consultation) => {
    const status = consultation.status;
    const paymentStatus = consultation.payment_status;

    if (paymentStatus === 'pending') {
      return { label: 'Complete Payment', path: `/consultation/${consultation.consultation_id}/payment` };
    }
    if (status === 'paid') {
      return { label: 'Answer Questions', path: `/consultation/${consultation.consultation_id}/questions` };
    }
    if (status === 'questions_completed') {
      return { label: 'View Diagnosis', path: `/consultation/${consultation.consultation_id}/diagnosis` };
    }
    if (status === 'diagnosed' || status === 'completed') {
      return { label: 'View Report', path: `/consultation/${consultation.consultation_id}/report` };
    }
    return null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPlanLabel = (plan) => {
    if (plan === 'consultation') return '1-on-1 Consultation';
    if (plan === 'verified') return 'Doctor-Reviewed';
    return 'AI Assessment';
  };

  // Group consultations by status
  const groupedByStatus = {
    'In Progress': consultations.filter(c => 
      c.payment_status === 'paid' && 
      c.status !== 'diagnosed' && 
      c.status !== 'completed'
    ),
    'Pending Payment': consultations.filter(c => c.payment_status === 'pending'),
    'Completed': consultations.filter(c => 
      c.status === 'diagnosed' || c.status === 'completed'
    )
  };

  // Group consultations by health issues
  const groupedByIssues = {};
  consultations.forEach(c => {
    const symptoms = c.symptoms || [];
    symptoms.forEach(symptom => {
      const issue = symptom.name || 'Other';
      if (!groupedByIssues[issue]) {
        groupedByIssues[issue] = [];
      }
      if (!groupedByIssues[issue].find(item => item.consultation_id === c.consultation_id)) {
        groupedByIssues[issue].push(c);
      }
    });
    if (symptoms.length === 0) {
      if (!groupedByIssues['Other']) groupedByIssues['Other'] = [];
      groupedByIssues['Other'].push(c);
    }
  });

  const ConsultationCard = ({ consultation }) => {
    const statusInfo = getStatusInfo(consultation);
    const nextAction = getNextAction(consultation);
    const StatusIcon = statusInfo.icon;
    
    return (
      <div 
        className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow"
        data-testid={`consultation-card-${consultation.consultation_id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-700`}>
              <StatusIcon className="w-3 h-3" />
              {statusInfo.label}
            </span>
            <span className="text-xs text-stone-400">
              #{consultation.consultation_id.slice(-6)}
            </span>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            consultation.plan === 'consultation' ? 'bg-purple-100 text-purple-700' :
            consultation.plan === 'verified' ? 'bg-blue-100 text-blue-700' :
            'bg-stone-100 text-stone-600'
          }`}>
            {getPlanLabel(consultation.plan)}
          </span>
        </div>
        
        <div className="mb-3">
          <p className="text-sm font-medium text-stone-800">
            {consultation.symptoms?.map(s => s.name).slice(0, 3).join(', ') || 'No symptoms recorded'}
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Created: {formatDate(consultation.created_at)}
          </p>
        </div>
        
        {nextAction && (
          <Button
            onClick={() => navigate(nextAction.path)}
            size="sm"
            className={consultation.payment_status === 'pending' ? 'btn-secondary w-full' : 'btn-primary w-full'}
          >
            {nextAction.label}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="dashboard">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://aiays.org/wp-content/uploads/2024/01/Untitled-13.png" 
              alt="Logo" 
              className="h-10 w-auto"
            />
            <span className="text-xl font-semibold text-[hsl(25,30%,35%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ayurveda Consult
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Button
                variant="ghost"
                onClick={() => navigate('/admin-portal')}
                className="text-stone-600 hover:text-[hsl(22,70%,44%)]"
                data-testid="admin-link"
              >
                <Settings className="w-5 h-5 mr-2" />
                Admin
              </Button>
            )}
            <div className="flex items-center gap-3">
              <img 
                src={user?.picture || 'https://via.placeholder.com/40'} 
                alt={user?.name}
                className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
              />
              <span className="text-stone-700 font-medium hidden sm:block">{user?.name}</span>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-stone-600 hover:text-red-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-stone-600">Manage your Ayurvedic consultations and wellness journey</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card">
            <p className="text-sm text-stone-500 mb-1">Total Consultations</p>
            <p className="text-2xl font-bold text-[hsl(120,10%,18%)]">{consultations.length}</p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-stone-500 mb-1">Completed</p>
            <p className="text-2xl font-bold text-emerald-600">
              {consultations.filter(c => c.status === 'diagnosed' || c.status === 'completed').length}
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-stone-500 mb-1">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">
              {consultations.filter(c => c.status !== 'diagnosed' && c.status !== 'completed' && c.payment_status === 'paid').length}
            </p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-stone-500 mb-1">Pending Payment</p>
            <p className="text-2xl font-bold text-amber-600">
              {consultations.filter(c => c.payment_status === 'pending').length}
            </p>
          </div>
        </div>

        {/* New Consultation Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your Consultations
          </h2>
          <div className="flex items-center gap-3">
            <Tabs value={groupBy} onValueChange={setGroupBy} className="hidden sm:block">
              <TabsList className="h-9">
                <TabsTrigger value="status" className="text-xs">By Status</TabsTrigger>
                <TabsTrigger value="issues" className="text-xs">By Issues</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button 
              onClick={() => navigate('/consultation/new')}
              className="btn-primary"
              data-testid="new-consultation-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Consultation
            </Button>
          </div>
        </div>

        {/* Consultations Display */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-stone-200 p-5 animate-pulse">
                <div className="h-6 bg-stone-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-stone-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-stone-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : consultations.length === 0 ? (
          <div className="card-elevated p-12 text-center">
            <div className="w-20 h-20 bg-[hsl(22,70%,44%)/0.1] rounded-full flex items-center justify-center mx-auto mb-6">
              <Leaf className="w-10 h-10 text-[hsl(22,70%,44%)]" />
            </div>
            <h3 className="text-xl font-semibold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Start Your First Consultation
            </h3>
            <p className="text-stone-600 mb-6 max-w-md mx-auto">
              Begin your personalized Ayurvedic wellness journey. Get AI-powered assessment and recommendations.
            </p>
            <Button 
              onClick={() => navigate('/consultation/new')}
              className="btn-primary"
              data-testid="start-first-consultation-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Start Consultation
            </Button>
          </div>
        ) : groupBy === 'status' ? (
          <div className="space-y-8">
            {Object.entries(groupedByStatus).map(([status, items]) => items.length > 0 && (
              <div key={status}>
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-lg font-semibold text-stone-700">{status}</h3>
                  <span className="px-2 py-0.5 bg-stone-100 rounded-full text-xs text-stone-500">
                    {items.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(consultation => (
                    <ConsultationCard key={consultation.consultation_id} consultation={consultation} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedByIssues).map(([issue, items]) => (
              <div key={issue}>
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-lg font-semibold text-stone-700">{issue}</h3>
                  <span className="px-2 py-0.5 bg-stone-100 rounded-full text-xs text-stone-500">
                    {items.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(consultation => (
                    <ConsultationCard key={consultation.consultation_id} consultation={consultation} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Useful Links Section */}
        <div className="mt-16 pt-8 border-t border-stone-200">
          <h3 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Quick Links & Resources
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <a 
              href="/panchkarma"
              onClick={(e) => { e.preventDefault(); navigate('/panchkarma'); }}
              className="flex flex-col items-center p-4 bg-white rounded-xl border border-stone-200 hover:shadow-md hover:border-[hsl(22,70%,44%)] transition-all group"
            >
              <MapPin className="w-8 h-8 text-[hsl(22,70%,44%)] mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-stone-700 text-center">Find Panchkarma Centre</span>
            </a>
            
            <a 
              href="https://tidycal.com/aiays/client-enquiry"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-white rounded-xl border border-stone-200 hover:shadow-md hover:border-[hsl(22,70%,44%)] transition-all group"
            >
              <Calendar className="w-8 h-8 text-[hsl(22,70%,44%)] mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-stone-700 text-center">Book a Call</span>
            </a>
            
            <a 
              href="https://www.aiays.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-white rounded-xl border border-stone-200 hover:shadow-md hover:border-[hsl(22,70%,44%)] transition-all group"
            >
              <Globe className="w-8 h-8 text-[hsl(22,70%,44%)] mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-stone-700 text-center">Visit Website</span>
            </a>
            
            <a 
              href="mailto:me@ayurvedaconsult.online"
              className="flex flex-col items-center p-4 bg-white rounded-xl border border-stone-200 hover:shadow-md hover:border-[hsl(22,70%,44%)] transition-all group"
            >
              <Mail className="w-8 h-8 text-[hsl(22,70%,44%)] mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-stone-700 text-center">Send Enquiry</span>
            </a>
            
            <a 
              href="https://aiays.org/wp-content/uploads/2024/09/Your-First-Step-to-Ayurveda.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-white rounded-xl border border-stone-200 hover:shadow-md hover:border-[hsl(22,70%,44%)] transition-all group"
            >
              <Download className="w-8 h-8 text-[hsl(22,70%,44%)] mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-stone-700 text-center">Download eBook</span>
            </a>
            
            <a 
              href="https://chat.whatsapp.com/JdHF4ogYHH95eZ4kGq9ZgM"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-white rounded-xl border border-stone-200 hover:shadow-md hover:border-[hsl(22,70%,44%)] transition-all group"
            >
              <MessageCircle className="w-8 h-8 text-green-600 mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-stone-700 text-center">Join WhatsApp</span>
            </a>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};
